import { useMemo, useState } from 'react'
import type {
	Loan,
	LoanShareholderWithApartment,
	TotalInterestSegment,
} from '@viilu-web/shared/types/loans'
import ShareCalculationFilters from './ShareCalculationFilters'
import styled from 'styled-components'
import { loanShareCalculationBlocks } from './constants'
import { FormattedMessage, useIntl } from 'react-intl'
import type useLoanShareCalculation from './useLoanShareCalculation'
import { formatCurrency } from 'util/formatStrings'
import { Button, ExpansionList, ExpansionPanel, FontIcon } from 'react-md'
import moment from 'moment'
import LoadingState from 'ui/components/LoadingState'
import LoanShareholdersTable from './LoanShareholdersTable'
import { useCompanyApartments } from 'util/hooks/useCompanyApartments'
import { useBuildings } from 'ui/CompanyBuildings/hooks'
import { createApartmentOptions } from '@viilu-web/shared/util/apartmentOptions'
import LoanShareCalculationCreationDialog from './LoanShareCalculationCreationDialog'
import naturalSort from 'javascript-natural-sort'
import type { LoanViewFilters } from '../types'
import useInvoices from 'ui/ManagerFinance/Invoices/useInvoices'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'

const ShareCalculationContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	gap: 1.5rem;
`

const ShareCalculationBlockContainer = styled.div``

const ShareCalculationBlockTitle = styled.h4``

const ShareCalculationRow = styled.div<{ $isRed?: boolean }>`
	${(props) => props.$isRed && 'color: var(--color-error);'}
	display: grid;
	padding: 0.25rem 0.5rem;
	gap: 0.5rem;
	grid-template-columns: 1fr 1fr;
`

const ShareCalculationRowValue = styled.div`
	text-align: right;
`

const InterestBlock = styled.div`
	text-align: left;
`

const InterestPanelContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-top: 0.5rem;
`

const InterestExpansionPanel = styled(ExpansionPanel)`
	margin-top: 0 !important;
	.md-panel-header {
		padding: 0.5rem;
		height: auto;

		.md-panel-column {
			padding-right: 0.5rem;
		}
	}
	.md-panel-content {
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		padding-bottom: 0.5rem;
		font-size: 15px;
	}
`

const ActionButtonContainer = styled.div`
	display: flex;
	flex-flow: row nowrap;
	justify-content: right;
	gap: 1rem;
	margin-bottom: 1rem;
`

const LoanInvoiceStatusBox = styled.div`
	display: flex;
	flex-flow: column nowrap;
	align-items: center;
	background: #fff;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
	padding: 0.5rem 1rem;
	margin-left: auto;
	font-size: 1rem;
	max-width: 350px;
`

const LoanInvoiceStatusBoxAndText = styled.div`
	display: flex;
	flex-direction: row nowrap;
	gap: 0.75rem;
	align-items: center;
`

const LoanInvoiceStatusButton = styled(Button)`
	padding: 0.25rem;
	height: auto;
`

type Props = {
	loan: Loan
	filters: LoanViewFilters
	setFilters: React.Dispatch<React.SetStateAction<LoanViewFilters>>
	loanShareCalculation: ReturnType<typeof useLoanShareCalculation>
}

function LoanShareCalculationView({
	loan,
	filters,
	setFilters,
	loanShareCalculation,
}: Props) {
	const intl = useIntl()
	const dispatch = useDispatch()
	const { calculation, loading } = loanShareCalculation
	const [selectedSharegroups, setSelectedSharegroups] = useState<string[]>([])
	const [
		createShareCalculationDialogVisible,
		setCreateShareCalculationDialogVisible,
	] = useState(false)
	const { companyApartments } = useCompanyApartments(loan.companyUUID)
	const { buildings } = useBuildings(loan.companyUUID)

	const apartmentOptions = createApartmentOptions(companyApartments, buildings)

	const invoiceFilters = useMemo(() => {
		return {
			paymentProductUUIDs: loan.paymentProductUUID
				? [loan.paymentProductUUID]
				: null,
			statuses: ['created', 'sent', 'paid'],
		}
	}, [loan.paymentProductUUID])

	const { data: invoices, loading: invoicesLoading } = useInvoices(
		loan.companyUUID,
		invoiceFilters,
	)

	const loanInvoiceStatus = useMemo(() => {
		if (invoices.length === 0 && invoicesLoading) return null
		if (invoices.length === 0)
			return {
				created: false,
			}
		if (invoices.length > 0) {
			const latestInvoice = invoices.reduce((latest, current) => {
				return new Date(current.invoiceDate) > new Date(latest.invoiceDate)
					? current
					: latest
			})
			return {
				status: true,
				latestInvoiceDate: latestInvoice.invoiceDate,
			}
		}
	}, [invoices, invoicesLoading])

	const shareholdersWithApartmentOptions: LoanShareholderWithApartment[] =
		useMemo(() => {
			return loan.shareholders
				.map((sh) => {
					const correspondingApartmentOption = apartmentOptions.find(
						(option) => option.value === sh.shareGroupUUID,
					)

					return {
						...sh,
						apartment: {
							uuid: correspondingApartmentOption?.value,
							label: correspondingApartmentOption?.label,
							identifier: correspondingApartmentOption?.identifier,
						},
					}
				})
				.sort((a, b) => naturalSort(a.apartment?.label, b.apartment?.label))
		}, [loan.shareholders, apartmentOptions])

	// Merges consecutive interest segments with the same interest rate for display purposes
	const mergeInterestSegments = (
		segments: TotalInterestSegment[],
	): TotalInterestSegment[] => {
		if (segments.length <= 1) return [...segments]

		const sortedSegments = [...segments].sort(
			(a, b) =>
				new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
		)

		return sortedSegments.reduce<TotalInterestSegment[]>((merged, segment) => {
			const last = merged[merged.length - 1]
			if (!last || last.interestRate !== segment.interestRate) {
				merged.push({ ...segment })
			}
			// If the interest rate is the same, we skip adding this segment
			return merged
		}, [])
	}

	const renderInterestPercentagePanel = (
		interestSegments: TotalInterestSegment[],
	) => {
		const mergedSegments = mergeInterestSegments(interestSegments)
		if (mergedSegments.length === 0) return 0
		if (mergedSegments.length === 1) {
			return `${mergedSegments[0].interestRate} %`
		}

		const interestSegmentsDescending = [...mergedSegments].reverse()
		const currentStartDate = moment(
			interestSegmentsDescending[0].startDate,
		).format('D.M.YYYY')

		return (
			<ExpansionList>
				<InterestExpansionPanel
					component="div"
					footer={null}
					label={
						<InterestBlock>
							<strong>{`${interestSegmentsDescending[0].interestRate} %`}</strong>
							<p>
								<FormattedMessage
									defaultMessage="alkaen {date}"
									description="begins at {date}"
									values={{
										date: currentStartDate,
									}}
								/>
							</p>
						</InterestBlock>
					}
				>
					<InterestPanelContent>
						{interestSegmentsDescending.slice(1).map((segment) => {
							const startDate = moment(segment.startDate).format('D.M.YYYY')
							return (
								<InterestBlock key={`${segment.startDate}`}>
									<strong>{`${segment.interestRate} %`}</strong>
									<p>
										<FormattedMessage
											defaultMessage="alkaen {date}"
											description="begins at {date}"
											values={{
												date: startDate,
											}}
										/>
									</p>
								</InterestBlock>
							)
						})}
					</InterestPanelContent>
				</InterestExpansionPanel>
			</ExpansionList>
		)
	}

	const getRowValue = (
		key: string,
	): { value: number; formatted: React.ReactNode } => {
		if (!calculation) return { value: 0, formatted: 0 }

		switch (key) {
			case 'loanDrawnAmount':
				return {
					value: calculation.loanDrawnAmount,
					formatted: formatCurrency(calculation.loanDrawnAmount),
				}
			case 'balance':
				return {
					value: calculation.debtAmount,
					formatted: formatCurrency(calculation.debtAmount),
				}
			case 'paymentIncome':
				return {
					value: calculation.paymentIncome,
					formatted: formatCurrency(calculation.paymentIncome),
				}
			case 'otherIncome':
				return {
					value: calculation.otherIncome,
					formatted: formatCurrency(calculation.otherIncome),
				}
			case 'repayments':
				return {
					value: calculation.totalRepayments,
					formatted: formatCurrency(calculation.totalRepayments),
				}
			case 'interestExpenses':
				return {
					value: calculation.accruedInterestAmount,
					formatted: formatCurrency(calculation.accruedInterestAmount),
				}
			case 'otherExpenses':
				return {
					value: calculation.otherExpenses,
					formatted: formatCurrency(calculation.otherExpenses),
				}
			case 'interestPercentage':
				return {
					value: calculation.accruedInterestSegments[0]?.interestRate ?? 0,
					formatted: renderInterestPercentagePanel(
						calculation.accruedInterestSegments,
					),
				}
			case 'baseBalance':
				return {
					value: calculation.baseBalance,
					formatted: formatCurrency(calculation.baseBalance),
				}
			case 'transferredBalance':
				return {
					value: calculation.transferredBalance,
					formatted: formatCurrency(calculation.transferredBalance),
				}
			case 'totalBalance':
				return {
					value: calculation.totalBalance,
					formatted: formatCurrency(calculation.totalBalance),
				}
			case 'totalUnits':
				return {
					value: calculation.totalUnits,
					formatted: calculation.totalUnits,
				}
			case 'loanShareAmount':
				return {
					value: calculation.loanShareAmount,
					formatted: formatCurrency(calculation.loanShareAmount),
				}
			case 'shareBaseBalance':
				return {
					value: calculation.shareBalance,
					formatted: formatCurrency(calculation.shareBalance),
				}
			case 'shareTotalBalance':
				return {
					value: calculation.totalShareBalance,
					formatted: formatCurrency(calculation.totalShareBalance),
				}
			default:
				return { value: 0, formatted: formatCurrency(0) }
		}
	}

	const renderShareCalculationRows = () => {
		return loanShareCalculationBlocks.map((block) => (
			<ShareCalculationBlockContainer key={block.key}>
				<ShareCalculationBlockTitle>
					{block.blockTitle(intl)}
				</ShareCalculationBlockTitle>
				{loading && <LoadingState />}
				{!loading &&
					block.rows.map((row) => {
						const { value, formatted } = getRowValue(row.key)
						const isNegative = value < 0

						return (
							<ShareCalculationRow key={row.key} $isRed={isNegative}>
								{row.title(intl, isNegative)}
								<ShareCalculationRowValue>{formatted}</ShareCalculationRowValue>
							</ShareCalculationRow>
						)
					})}
			</ShareCalculationBlockContainer>
		))
	}

	return (
		<div>
			{loading || invoicesLoading ? (
				<LoadingState />
			) : (
				<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
					<ShareCalculationFilters filters={filters} setFilters={setFilters} />
					<LoanInvoiceStatusBox>
						{loanInvoiceStatus?.status ? (
							<LoanInvoiceStatusBoxAndText>
								<FontIcon style={{ color: 'green', fontSize: '2rem' }}>
									check_circle
								</FontIcon>
								<span style={{ fontWeight: 500 }}>
									<p>
										<FormattedMessage
											defaultMessage="Lainan pääomavastikkeen laskut on luotu {date} asti."
											description="Loan principal charge has been invoiced"
											values={{
												date: moment(
													loanInvoiceStatus?.latestInvoiceDate,
												).format('D.M.YYYY'),
											}}
										/>
									</p>
									<LoanInvoiceStatusButton
										flat
										component="a"
										secondary
										onClick={() => {
											dispatch(push('/manager/finance/invoices'))
										}}
									>
										<FormattedMessage
											defaultMessage="Siirry laskuihin"
											description="Move to invoices"
										/>
									</LoanInvoiceStatusButton>
								</span>
							</LoanInvoiceStatusBoxAndText>
						) : (
							<LoanInvoiceStatusBoxAndText>
								<FontIcon style={{ color: 'red', fontSize: '2rem' }}>
									error
								</FontIcon>
								<span style={{ fontWeight: 500 }}>
									<p>
										<FormattedMessage
											defaultMessage="Lainan pääomavastikkeen laskuja ei ole vielä luotu. Luo laskut, jotta vastiketuotot voidaan laskea."
											description="Loan principal charge invoices have not yet been created. Create invoices to calculate charge revenues."
										/>
									</p>

									<LoanInvoiceStatusButton
										flat
										component="a"
										secondary
										onClick={() => {
											dispatch(push('/manager/finance/invoices'))
										}}
									>
										<FormattedMessage
											defaultMessage="Siirry laskun luontiin"
											description="Move to invoice creation"
										/>
									</LoanInvoiceStatusButton>
								</span>
							</LoanInvoiceStatusBoxAndText>
						)}
					</LoanInvoiceStatusBox>
				</div>
			)}
			<ShareCalculationContainer>
				{renderShareCalculationRows()}
			</ShareCalculationContainer>
			{loan?.shareholders && calculation && (
				<>
					<ActionButtonContainer>
						<Button
							raised
							secondary
							disabled={selectedSharegroups.length === 0}
							onClick={() => setCreateShareCalculationDialogVisible(true)}
						>
							<FormattedMessage
								defaultMessage="Muodosta lainaosuuslaskelma"
								description="Button label for forming loan share calculation"
							/>
						</Button>
					</ActionButtonContainer>
					<LoanShareholdersTable
						shareholdersWithApartmentOptions={shareholdersWithApartmentOptions}
						sharegroupDebts={calculation?.shareholderCalculations}
						selectedShareholders={selectedSharegroups}
						setSelectedShareholders={setSelectedSharegroups}
					/>
				</>
			)}
			{createShareCalculationDialogVisible && (
				<LoanShareCalculationCreationDialog
					onHide={() => setCreateShareCalculationDialogVisible(false)}
					loan={loan}
					initialStartDate={filters.startDate}
					initialEndDate={filters.endDate}
					selectedShareholders={shareholdersWithApartmentOptions.filter(
						(shareholder) => selectedSharegroups.includes(shareholder.uuid),
					)}
				/>
			)}
		</div>
	)
}

export default LoanShareCalculationView
